/**
 * Infused Nirvana — Dynamic Add to Cart button label for the Recharge Subscriptions Widget
 * ------------------------------------------------------------------------------------------
 * Makes the Add to Cart button read "Add Subscription" when "Subscribe & save" is the
 * selected purchase option, and "Add to Cart" when "One-time" is selected — for any
 * product page that has Recharge's Subscription Widget v2 (<recharge-subscription-widget>)
 * on it. Products with no subscription option are unaffected.
 *
 * WHY THIS IS NEEDED: Recharge does not offer a built-in setting for this (confirmed via
 * their docs and support articles on the Subscription Widget, Theme Engine, and widget
 * event system — none expose a "purchase option changed" hook or a button-label toggle).
 * This script fills that gap with a small, self-contained integration.
 *
 * HOW IT WORKS: The widget's "Subscribe & save" / "One-time" radio inputs live inside the
 * <recharge-subscription-widget> element's shadow DOM (name="purchaseOption", values
 * "subscription" / "onetime"). Their native input/change events are composed, so they
 * bubble out of the shadow DOM and can be caught on `document`, re-targeted at the
 * <recharge-subscription-widget> host element — no need to reach into the shadow root
 * except to read which radio is currently checked.
 *
 * Verified live against https://infusednirvana.com/products/sips-straws on 2026-08-18.
 *
 * INSTALL:
 *   1. Add this file as a new theme asset, e.g. assets/recharge-atc-label.js
 *   2. Load it once, site-wide (product pages only if you prefer), near the end of
 *      theme.liquid:
 *        <script src="{{ 'recharge-atc-label.js' | asset_url }}" defer></script>
 *      (Or paste the contents directly inside a <script> tag before </body>.)
 *   3. Test on a duplicate/unpublished theme first, then publish.
 *
 * CUSTOMIZE: Change SUBSCRIBE_LABEL / ONE_TIME_LABEL below if you want different copy.
 */
(function () {
  var SUBSCRIBE_LABEL = 'Add Subscription ✓';
  var ONE_TIME_LABEL = 'Add to Cart ✓';
 
  function findAddToCartButton(widgetEl) {
    // Scope to the nearest product-info container so this behaves correctly when more
    // than one product form/widget is on the page at once (bundles, quick-view modals).
    var container =
      widgetEl.closest('[id^="ProductInformation-"]') ||
      widgetEl.closest('form[action*="/cart/add"]') ||
      widgetEl.closest('.product, .product-form, [data-product-form]') ||
      document;
    return container.querySelector('button[name="add"]');
  }
 
  function setButtonLabel(button, isSubscription) {
    if (!button) return;
    var label = isSubscription ? SUBSCRIBE_LABEL : ONE_TIME_LABEL;
    var span = button.querySelector('span');
    if (span) {
      span.textContent = label;
    } else {
      button.textContent = label;
    }
  }
 
  function syncButton(widgetEl) {
    var shadow = widgetEl.shadowRoot;
    if (!shadow) return;
    var checked = shadow.querySelector('input[type="radio"]:checked');
    if (!checked) return;
    setButtonLabel(findAddToCartButton(widgetEl), checked.value === 'subscription');
  }
 
  function initWidget(widgetEl) {
    // The widget can finish hydrating its shadow DOM slightly after it's added to the
    // page, so retry briefly until the radio inputs exist, then sync the button once.
    var attempts = 0;
    (function trySync() {
      attempts++;
      if (widgetEl.shadowRoot && widgetEl.shadowRoot.querySelector('input[type="radio"]')) {
        syncButton(widgetEl);
      } else if (attempts < 20) {
        setTimeout(trySync, 150);
      }
    })();
  }
 
  // Catch every future selection change, site-wide.
  document.addEventListener(
    'input',
    function (e) {
      if (e.target && e.target.tagName === 'RECHARGE-SUBSCRIPTION-WIDGET') {
        syncButton(e.target);
      }
    },
    true
  );
 
  function scanForWidgets(root) {
    if (!root.querySelectorAll) return;
    root.querySelectorAll('recharge-subscription-widget').forEach(initWidget);
  }
 
  function boot() {
    scanForWidgets(document);
  }
 
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
  }
 
  // Re-sync when Shopify/the theme re-renders the product form or widget (variant
  // changes, quick-view opens, etc.).
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'RECHARGE-SUBSCRIPTION-WIDGET') {
          initWidget(node);
        } else {
          scanForWidgets(node);
        }
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();